import { useState } from 'react'
import { toast } from 'sonner'
import { Lock } from 'lucide-react'
import { departamentulRolului, type DepartamentSlug } from '@b4l/shared'
import { useAuthStore } from '../../stores/auth'
import { Modal } from '../../components/ui/Modal'
import { Button } from '../../components/ui/Button'
import {
  AlegeDepartamente,
  listaDepartamente,
  trimiteLa,
} from '../../components/taskuri/AlegeDepartamente'
import { ListaOameni, useOameni } from './AlegeOameni'
import { numeOameni, useMaiDeparte, useUrmarescPrivatele } from './date'

/**
 * Un task PRIVAT trimis mai departe (Madalin, 19.10.2026: Victor, cu ce-i vine din contact@,
 * „flexibilitate maxima"). Aceeasi alegere ca la „Task nou" — Departamente sau Responsabili —, ca
 * omul sa invete o singura regula:
 *
 *   - Departamente: iese la vedere, la „De preluat" (la mai multe: cate un task, legate);
 *   - Responsabili + „Privat": ramane privat, la ei; tu il vezi in continuare („Date de tine altora");
 *   - Responsabili fara „Privat": la vedere, direct „În lucru" la ei, pe tabloul departamentului ales.
 *
 * Tu iesi de pe el, in afara de cazul in care te bifezi.
 */
export function ModalMaiDeparte({
  task,
  departamentInitial,
  laInchidere,
  laTrimis,
}: {
  task: { id: number; titlu: string }
  /** Din propunerea filtrului: „Trimite la Vânzări" deschide direct pe departament. */
  departamentInitial: DepartamentSlug | null
  laInchidere: () => void
  laTrimis: () => void
}) {
  const eu = useAuthStore((s) => s.user?.id)
  const [mod, setMod] = useState<'departamente' | 'responsabili'>('departamente')
  const [alese, setAlese] = useState<DepartamentSlug[]>(
    departamentInitial ? [departamentInitial] : [],
  )
  const [oameni, setOameni] = useState<number[]>([])
  const [privat, setPrivat] = useState(true)
  const [depAles, setDepAles] = useState<DepartamentSlug | null>(null)
  const [motiv, setMotiv] = useState('')
  const toti = useOameni()
  const urmaresc = useUrmarescPrivatele()
  // Fara cei deja numiti: tu si cei alesi.
  const urmarescAltii = urmaresc.numeFara([eu, ...oameni])
  const trimite = useMaiDeparte()

  const nume = numeOameni(
    oameni.map((id) => ({
      name: id === eu ? 'tine' : (toti.data?.users.find((u) => u.id === id)?.name ?? ''),
    })),
  )
  const primul = toti.data?.users.find((u) => u.id === oameni[0])
  const depResponsabili = depAles ?? (primul ? departamentulRolului(primul.role) : null)

  const gata =
    mod === 'departamente' ? alese.length > 0 : oameni.length > 0 && (privat || !!depResponsabili)

  const eticheta =
    mod === 'departamente'
      ? trimiteLa(alese)
      : !oameni.length
        ? 'Alege responsabilii'
        : !privat && !depResponsabili
          ? 'Alege departamentul'
          : `Dă-l lui ${nume}`

  const trimis = () => {
    toast.success(
      mod === 'departamente'
        ? `Trimis la ${listaDepartamente(alese)}.`
        : privat
          ? `Dat lui ${nume}, rămâne privat.`
          : `Dat lui ${nume}.`,
    )
    laTrimis()
  }

  return (
    <Modal
      open
      onClose={laInchidere}
      title="Trimite mai departe"
      footer={
        <>
          <Button variant="secondary" onClick={laInchidere}>
            Renunță
          </Button>
          <Button
            disabled={!gata}
            loading={trimite.isPending}
            onClick={() =>
              trimite.mutate(
                mod === 'departamente'
                  ? { id: task.id, privat: false, departamente: alese, motiv: motiv.trim() || null }
                  : privat
                    ? { id: task.id, privat: true, oameni, motiv: motiv.trim() || null }
                    : {
                        id: task.id,
                        privat: false,
                        oameni,
                        departamente: [depResponsabili!],
                        motiv: motiv.trim() || null,
                      },
                { onSuccess: trimis },
              )
            }
          >
            {eticheta}
          </Button>
        </>
      }
    >
      <div className="grid gap-3.5">
        <p className="text-mic text-b4l-text-muted">„{task.titlu}”</p>
        <div className="grid grid-cols-2 gap-1.5" role="radiogroup" aria-label="Trimiți la">
          {(
            [
              ['departamente', 'Departamente', 'iese la vedere, îl preia cine are timp'],
              ['responsabili', 'Responsabili', 'direct la unul sau mai mulți colegi'],
            ] as const
          ).map(([k, titlu, sub]) => (
            <button
              key={k}
              type="button"
              role="radio"
              aria-checked={mod === k}
              onClick={() => setMod(k)}
              className={`grid rounded-caseta border px-3 py-2 text-left ${
                mod === k
                  ? 'border-b4l-blue bg-b4l-blue-light'
                  : 'border-b4l-border bg-b4l-surface hover:border-b4l-blue'
              }`}
            >
              <b className="text-corp font-semibold text-b4l-text">{titlu}</b>
              <span className="text-mic text-b4l-text-muted">{sub}</span>
            </button>
          ))}
        </div>

        {mod === 'departamente' ? (
          <div className="grid gap-1.5">
            <AlegeDepartamente alese={alese} setAlese={setAlese} eticheta="Departamente" />
            <span className="text-mic text-b4l-text-muted">
              {alese.length > 1
                ? `Pleacă câte un task la ${listaDepartamente(alese)}, cu documentele. Intră la „De preluat” în fiecare.`
                : 'Intră la „De preluat”, cu emailul, discuția și documentele. Tu ieși de pe el.'}
            </span>
          </div>
        ) : (
          <div className="grid gap-1.5">
            <ListaOameni
              alesi={oameni}
              setAlesi={setOameni}
              departament={depResponsabili}
              eticheta="Responsabili"
            />
            <label className="mt-1 inline-flex cursor-pointer items-start gap-2 text-corp text-b4l-text">
              <input
                type="checkbox"
                checked={privat}
                onChange={(e) => setPrivat(e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-b4l-purple"
              />
              <span>
                <span className="inline-flex items-center gap-1 font-semibold text-b4l-purple-dark">
                  <Lock size={13} aria-hidden="true" /> Rămâne privat
                </span>{' '}
                <span className="text-b4l-text-muted">
                  — îl văd doar ei și tu
                  {urmarescAltii ? ` (și ${urmarescAltii}, care urmărește privatele)` : ''}; îl
                  găsești în „Ale mele”, la „Date de tine altora”
                </span>
              </span>
            </label>
            {!privat && (
              <div className="grid gap-1 rounded-caseta bg-b4l-bg-soft px-3 py-2 text-mic">
                <span className="text-b4l-text-muted">Îl vede și departamentul, pe tablou:</span>
                <AlegeDepartamente
                  alese={depResponsabili ? [depResponsabili] : []}
                  setAlese={(d) => setDepAles(d[0] ?? null)}
                  multe={false}
                  eticheta="Departamentul în care apare"
                />
              </div>
            )}
          </div>
        )}

        <label className="grid gap-1.5">
          <span className="text-mic font-semibold text-b4l-text-muted">
            Notă <span className="font-normal text-b4l-text-dim">(opțional, apare în traseu)</span>
          </span>
          <input
            className="w-full rounded-caseta border border-b4l-border bg-b4l-surface px-2.5 py-2 text-corp text-b4l-text"
            maxLength={500}
            value={motiv}
            onChange={(e) => setMotiv(e.target.value)}
            placeholder="ex: e o cerere de ofertă, se ocupă Adi"
          />
        </label>
      </div>
    </Modal>
  )
}
